import { CombinedReleaseNote } from '@/databuilders/wp_dc_recentchanges'
import { Syllabus } from '@/kontent/content-types'
import { TaxoSyllabus } from '@/kontent/taxonomies'
import CustomAccordion, {
	CustomAccordionProps,
} from '@/legacy-ported/components/custom/CustomAccordion'
import { IPropWithClassNameChildren, Mapping } from '@/types'
import {
	compareValueWithMultipleChoiceCodename,
	getStageTags,
	getUrlFromMapping,
} from '@/utils'
import { getReleaseNoteTitle } from '@/utils/ace/getReleaseNoteTitle'
import {
	isReleasenoteAceKla,
	isReleasenoteAceSyllabus,
	isReleasenoteSyllabus,
	isReleasenoteSyllabusKla,
	isReleasenoteSyllabusMultiple,
} from '@/utils/type_predicates'
import { IContentItemsContainer } from '@kontent-ai/delivery-sdk'
import clsx from 'clsx'
import format from 'date-fns/format'
import { TagList } from 'nsw-ds-react'
import { NavItem } from 'nsw-ds-react/dist/component/main-nav/mainNav'
import { TagProps } from 'nsw-ds-react/dist/component/tags/tags'
import { ReactNode } from 'react'
import RichText from '../RichText'
import { getLinkedItems } from '../contexts/KontentHomeConfigProvider'
import { ReleaseNoteAffectedSyllabus } from './ReleaseNoteAffectedSyllabus'

export interface ReleaseNoteAccordionProps
	extends Pick<CustomAccordionProps, 'expanded'> {
	releaseNote: CombinedReleaseNote
	mappings: Mapping[]
	linkedItems: IContentItemsContainer
	syllabusTaxoSyllabusUrls: Record<TaxoSyllabus, NavItem>
	slotBeforeBodyContent?: ReactNode
	syllabuses?: Syllabus[]
}

export const getStageTagsForReleaseNoteAccordion = (
	releaseNote: CombinedReleaseNote,
) => {
	let lifeSkillStage: TagProps[] = []
	if (
		isReleasenoteAceSyllabus(releaseNote) ||
		isReleasenoteAceKla(releaseNote) ||
		isReleasenoteSyllabusKla(releaseNote) ||
		isReleasenoteSyllabusMultiple(releaseNote) ||
		isReleasenoteSyllabus(releaseNote)
	) {
		if (
			compareValueWithMultipleChoiceCodename(
				releaseNote.elements?.syllabus_type__items,
				'life_skills',
			)
		) {
			lifeSkillStage.push({
				text: 'Life Skills',
			})
		}
	}
	return [
		...getStageTags(releaseNote.elements.stages__stages),
		...lifeSkillStage,
	].filter(Boolean)
}

export const getNavItemFromSyllabusTaxo = (
	syllabusTaxo: TaxoSyllabus,
	syllabusTaxoSyllabusUrls: Record<TaxoSyllabus, NavItem>,
) => {
	return syllabusTaxoSyllabusUrls[syllabusTaxo]
}

export const ReleaseNoteAccordionBody = (
	props: Omit<ReleaseNoteAccordionProps, 'expanded'> &
		IPropWithClassNameChildren & {
			richtextClassName?: string
			slotBeforeContent?: ReactNode
			syllabuses?: Syllabus[]
		},
) => {
	const {
		releaseNote,
		mappings,
		linkedItems,
		syllabusTaxoSyllabusUrls,
		richtextClassName,
		className,
		slotBeforeContent,
		syllabuses,
	} = props
	const _isReleaseNoteAce =
		isReleasenoteAceKla(releaseNote) ||
		isReleasenoteAceSyllabus(releaseNote)

	const aceSubgroupUrl = _isReleaseNoteAce
		? getUrlFromMapping(mappings, releaseNote.elements.subgroup.value[0])
		: null
	const stageTags = getStageTagsForReleaseNoteAccordion(releaseNote)

	const affecteds: NavItem[] = [
		_isReleaseNoteAce &&
			aceSubgroupUrl &&
			({
				id: 'ace-subgroup',
				text: getLinkedItems(
					releaseNote.elements.subgroup,
					linkedItems,
				)?.[0]?.elements.title.value,
				url: aceSubgroupUrl,
				target: undefined,
			} as NavItem),
	].filter(Boolean)

	return (
		<div className={clsx('space-y-8', className)}>
			{slotBeforeContent}

			<RichText
				className={richtextClassName}
				richTextElement={releaseNote.elements.content}
				mappings={mappings}
				linkedItems={linkedItems}
				data-kontent-item-id={releaseNote.system.id}
				data-kontent-element-codename="content"
			/>

			<TagList tags={stageTags}></TagList>

			<ReleaseNoteAffectedSyllabus
				title="Affects:"
				releaseNote={releaseNote}
				syllabusTaxoSyllabusUrls={syllabusTaxoSyllabusUrls}
				listItemsBeforeDefault={affecteds}
				syllabuses={syllabuses}
			/>
		</div>
	)
}

export const ReleaseNoteAccordion = (props: ReleaseNoteAccordionProps) => {
	const {
		releaseNote,
		expanded,
		mappings,
		linkedItems,
		syllabusTaxoSyllabusUrls,
		slotBeforeBodyContent,
		syllabuses,
	} = props

	const title = getReleaseNoteTitle(releaseNote, linkedItems)

	return (
		<CustomAccordion
			key={releaseNote.system.id}
			title={title}
			subTitle={format(
				new Date(releaseNote.elements.releasedate.value),
				'dd MMM yyyy',
			)}
			expanded={expanded}
		>
			<ReleaseNoteAccordionBody
				linkedItems={linkedItems}
				mappings={mappings}
				releaseNote={releaseNote}
				syllabusTaxoSyllabusUrls={syllabusTaxoSyllabusUrls}
				slotBeforeContent={slotBeforeBodyContent}
				syllabuses={syllabuses}
			/>
		</CustomAccordion>
	)
}
